import { JEFE_4 } from '../../../data/boss-objects';
import { GRAVITY, SIM_HZ, VIEW_WIDTH } from '../../../data/tuning';
import { createWallSearchlight, stepWallSearchlight } from '../../enemies/wall-searchlight';
import { LAUNCH_SPEED } from '../../enemies/tunnel';
import { clearPhaseObjects, spawnObject, type PatternEnv } from '../attacks';
import { BARREL_TICKS, createChato, crossingTicks, HIDDEN_TICKS, HIDING_TICKS, nextTunnel, mouthY, OUT_TICKS, WARNING_TICKS, type ChatoState } from '../chato';
import type { BossFight } from '../boss-fight';
import { registerPattern } from '../patterns';
import type { TickContext } from '../../tick';

/**
 * Attack patterns of JEFE-4 El Chato (GDD §9.2, issue #133). Each is called once per tick while its phase is active and the
 * boss is not in transition, so nothing happens during one. Entering a phase clears what the previous one left: the barrels,
 * the wall searchlights and the position of El Chato in his cycle.
 */

function syncPhase(fight: BossFight, env: PatternEnv): ChatoState {
  const { attacks } = env;
  if (attacks.phase !== fight.phase) {
    // The first call of the fight (phase 0) has nothing to clear; later ones clear what the phase before left.
    if (attacks.phase !== 0) clearPhaseObjects(attacks);
    attacks.phase = fight.phase;
    attacks.cadence = 0;
    attacks.pending = [];
    attacks.phaseTicks = 0;
    attacks.lastCentre = null;
    attacks.checks = 0;
  }
  attacks.phaseTicks += 1;
  attacks.chato ??= createChato();
  return attacks.chato;
}

const go = (chato: ChatoState, state: ChatoState['state']) => {
  chato.state = state;
  chato.ticks = 0;
};

/** A barrel leaves the mouth of the tunnel in an arc to the left: up to y 8 from the floor, down from the ceiling (EN-43 ballistics). */
function throwBarrel(fight: BossFight, context: TickContext, env: PatternEnv, chato: ChatoState): void {
  const { attacks, run } = env;
  const fromFloor = chato.tunnel === 'floor';
  spawnObject(attacks, context, 'barrel', {
    x: fight.x,
    y: mouthY(chato.tunnel) + (fromFloor ? 0.5 : -0.5),
    vx: (run.scrollSpeed - JEFE_4.barrelSpeed) / SIM_HZ,
    vy: fromFloor ? LAUNCH_SPEED / SIM_HZ : 0,
    gravity: GRAVITY / (SIM_HZ * SIM_HZ),
  });
  chato.barrels += 1;
  context.emit({ type: 'boss:barrel', tunnel: chato.tunnel });
}

/**
 * One tick of the cycle of El Chato. `peek` exits through a tunnel of the floor or the ceiling throwing `barrels`;
 * otherwise he crosses on the motorbike along the horizontal tunnel at ground level.
 */
function stepCycle(fight: BossFight, context: TickContext, env: PatternEnv, chato: ChatoState, options: { moto: boolean; barrels: number }): void {
  const { attacks, run, view } = env;
  chato.ticks += 1;
  // He stands at the mouth of the tunnel at 80 % of the view; on the motorbike he crosses the view from right to left.
  fight.y = mouthY(chato.tunnel);
  switch (chato.state) {
    case 'HIDDEN':
      if (chato.ticks >= HIDDEN_TICKS) {
        chato.tunnel = options.moto ? 'horizontal' : nextTunnel(chato, attacks.rng);
        chato.exits += 1;
        chato.barrels = 0;
        go(chato, 'WARNING');
        context.emit({ type: 'boss:warning', tunnel: chato.tunnel, x: fight.x, y: mouthY(chato.tunnel) });
      }
      break;
    case 'WARNING':
      if (chato.ticks >= WARNING_TICKS) {
        go(chato, options.moto ? 'MOTO' : 'OUT');
        context.emit({ type: options.moto ? 'boss:moto' : 'boss:out', tunnel: chato.tunnel });
      }
      break;
    case 'OUT':
      if (BARREL_TICKS.slice(0, options.barrels).includes(chato.ticks)) throwBarrel(fight, context, env, chato);
      if (chato.ticks >= OUT_TICKS) {
        go(chato, 'HIDING');
        context.emit({ type: 'boss:hidden', tunnel: chato.tunnel });
      }
      break;
    case 'MOTO': {
      // From just beyond the right edge to just beyond the left one, at the scroll speed plus 4 u/s.
      const total = crossingTicks(run.scrollSpeed, VIEW_WIDTH);
      fight.x = view.right + 2 - ((VIEW_WIDTH + 4) * chato.ticks) / total;
      if (chato.ticks >= total) {
        go(chato, 'HIDING');
        context.emit({ type: 'boss:hidden', tunnel: chato.tunnel });
      }
      break;
    }
    case 'HIDING':
      if (chato.ticks >= HIDING_TICKS) go(chato, 'HIDDEN');
      break;
  }
}

/** Phase 1, "Topo": out of a tunnel announced with dust, two barrels in an arc, and back in. */
registerPattern('topo', (fight, context, env) => {
  if (!env) return;
  const chato = syncPhase(fight, env);
  stepCycle(fight, context, env, chato, { moto: false, barrels: 2 });
});

/** Phase 2, "Moto": a crossing along the horizontal tunnel at ground level; the lane from y 1 to y 3 is harmful meanwhile. */
registerPattern('moto', (fight, context, env) => {
  if (!env) return;
  const chato = syncPhase(fight, env);
  stepCycle(fight, context, env, chato, { moto: true, barrels: 0 });
});

/** Phase 3, "El Muro": two wall searchlights in sync, gap columns closing from 4.0 u to 3.2 u, and El Chato peeking out. */
registerPattern('el-muro', (fight, context, env) => {
  if (!env) return;
  const chato = syncPhase(fight, env);
  const { attacks, run, view } = env;
  stepCycle(fight, context, env, chato, { moto: false, barrels: 1 });

  // The searchlights hang from the ceiling at fixed points of the view; they sweep in step (the same group of EN-44).
  if (attacks.searchlights.length === 0) {
    for (const fromView of JEFE_4.searchlightFromView) attacks.searchlights.push({ fromView, x: view.left + fromView * VIEW_WIDTH, y: JEFE_4.searchlightY, state: createWallSearchlight({ syncGroup: 1 }) });
  }
  for (const light of attacks.searchlights) {
    light.x = view.left + light.fromView * VIEW_WIDTH;
    const step = stepWallSearchlight(light.state, light, run.core.player, run.core.tick, run.core.bribeRemaining > 0);
    if (step.amber) context.emit({ type: 'en44:amber', summoned: true });
    if (step.burst) context.emit({ type: 'en44:burst', summoned: true });
  }

  // A column every 7 u: the gap closes with the phase from 4.0 u to 3.2 u in 30 s and stays there.
  const every = Math.round((JEFE_4.wallSpacing / run.scrollSpeed) * SIM_HZ);
  attacks.cadence += 1;
  if (attacks.pieces > 0 && attacks.cadence < every) return;
  attacks.cadence = 0;
  const previous = attacks.lastCentre ?? (JEFE_4.wallCentreLow + JEFE_4.wallCentreHigh) / 2;
  const wanted = JEFE_4.wallCentreLow + attacks.rng() * (JEFE_4.wallCentreHigh - JEFE_4.wallCentreLow);
  const centre = Math.min(JEFE_4.wallCentreHigh, Math.max(JEFE_4.wallCentreLow, Math.min(previous + JEFE_4.wallMaxStep, Math.max(previous - JEFE_4.wallMaxStep, wanted))));
  attacks.lastCentre = centre;
  attacks.pieces += 1;
  spawnObject(attacks, context, 'check-column', {
    x: view.right + JEFE_4.wallWidth,
    y: 6,
    check: { centre, gap: { start: JEFE_4.wallGapStart, end: JEFE_4.wallGapEnd, seconds: JEFE_4.wallCloseSeconds }, width: JEFE_4.wallWidth },
  });
});

